import React, { useState, useEffect, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import {
  Text, Flex, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalCloseButton, ModalBody, useDisclosure, Table, Thead, Tbody,
  Tr, Th, Td, Badge, Spinner
} from '@chakra-ui/react';
import ChartCard from './ChartCard';
import { analyticsService, UnitInterestResponse, UnitInterestEntry } from '../../services/analyticsService';
import { CHART_COLORS } from '../../utils/chartColors';

interface UnitInterestDonutProps {
  businessIdentifier?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';
const MIN_PERCENTAGE_THRESHOLD = 3;
const MAX_SLICES = 8;

type ClientEntry = { conversation_id: string; client_name: string };
type ClientsCache = Record<string, ClientEntry[]>;

const groupSmallEntries = (entries: UnitInterestEntry[]): UnitInterestEntry[] => {
  if (entries.length <= MAX_SLICES) {
    const totalCount = entries.reduce((sum, e) => sum + e.count, 0);
    const significant = entries.filter(e => (e.count / totalCount) * 100 >= MIN_PERCENTAGE_THRESHOLD);
    const small = entries.filter(e => (e.count / totalCount) * 100 < MIN_PERCENTAGE_THRESHOLD);
    if (small.length <= 1) return entries;
    const othersCount = small.reduce((sum, e) => sum + e.count, 0);
    return [...significant, { unit_type: `Others (${small.length} types)`, count: othersCount, percentage: Math.round((othersCount / totalCount) * 1000) / 10 }];
  }
  const sorted = [...entries].sort((a, b) => b.count - a.count);
  const top = sorted.slice(0, MAX_SLICES - 1);
  const rest = sorted.slice(MAX_SLICES - 1);
  const totalCount = entries.reduce((sum, e) => sum + e.count, 0);
  const othersCount = rest.reduce((sum, e) => sum + e.count, 0);
  return [...top, { unit_type: `Others (${rest.length} types)`, count: othersCount, percentage: Math.round((othersCount / totalCount) * 1000) / 10 }];
};

const UnitInterestDonut: React.FC<UnitInterestDonutProps> = ({ businessIdentifier }) => {
  const [data, setData] = useState<UnitInterestResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Drill-down
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedUnit, setSelectedUnit] = useState<string>('');
  const [clients, setClients] = useState<ClientEntry[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [clientsCache, setClientsCache] = useState<ClientsCache>({});

  const [preloaded, setPreloaded] = useState(false);

  useEffect(() => {
    if (!businessIdentifier) { setIsLoading(false); return; }
    setPreloaded(false);
    setClientsCache({});

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await analyticsService.getUnitInterestDistribution(businessIdentifier);
        setData(result);
        setIsLoading(false); // Show chart immediately

        // Preload clients in background AFTER chart is visible
        if (result.unit_types && result.unit_types.length > 0) {
          const topTypes = result.unit_types.slice(0, 8).filter(t => !t.unit_type.startsWith('Others'));
          const cache: ClientsCache = {};
          const promises = topTypes.map(async (t) => {
            try {
              const r = await analyticsService.getUnitTypeClients(businessIdentifier, t.unit_type);
              cache[t.unit_type] = r.clients || [];
            } catch { /* ignore */ }
          });
          await Promise.allSettled(promises);
          setClientsCache(cache);
          setPreloaded(true);
        } else {
          setPreloaded(true);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch unit interest data');
        setIsLoading(false);
      }
    };
    fetchData();
  }, [businessIdentifier]);

  // Check if a unit type has real client names (not just "Client (xxx...)" fallbacks)
  const hasRealClients = useCallback((unitType: string): boolean => {
    const cached = clientsCache[unitType];
    if (!cached || cached.length === 0) return false;
    return cached.some(c => !c.client_name.startsWith('Client ('));
  }, [clientsCache]);

  const handleSliceClick = useCallback(async (unitType: string) => {
    if (!businessIdentifier || unitType.startsWith('Others')) return;
    if (!preloaded) return; // Wait until preload is complete
    if (!hasRealClients(unitType)) return; // No real names, no click

    setSelectedUnit(unitType);
    onOpen();

    // Check cache first
    if (clientsCache[unitType]) {
      setClients(clientsCache[unitType].filter(c => !c.client_name.startsWith('Client (')));
      setIsLoadingClients(false);
      return;
    }

    // Fallback: fetch on demand
    setIsLoadingClients(true);
    setClients([]);
    try {
      const result = await analyticsService.getUnitTypeClients(businessIdentifier, unitType);
      const allClients = result.clients || [];
      setClientsCache(prev => ({ ...prev, [unitType]: allClients }));
      const namedClients = allClients.filter(c => !c.client_name.startsWith('Client ('));
      if (namedClients.length === 0) { onClose(); return; }
      setClients(namedClients);
    } catch { /* ignore */ } finally {
      setIsLoadingClients(false);
    }
  }, [businessIdentifier, clientsCache, hasRealClients, preloaded, onOpen, onClose]);

  const getChartOption = () => {
    if (!data || !data.unit_types || data.unit_types.length === 0) return {};
    const grouped = groupSmallEntries(data.unit_types);
    const pieData = grouped.map((entry, idx) => ({
      value: entry.count,
      name: entry.unit_type,
      itemStyle: {
        color: entry.unit_type.startsWith('Others') ? '#CBD5E0'
          : CHART_COLORS.palette[idx % CHART_COLORS.palette.length],
      },
    }));

    return {
      tooltip: {
        trigger: 'item' as const, textStyle: { fontFamily: FONT_FAMILY },
        formatter: (params: any) => {
          const clickable = preloaded && !params.name.startsWith('Others') && hasRealClients(params.name);
          return `<b>${params.name}</b><br/>Mentions: ${params.value}<br/>Share: ${params.percent?.toFixed(1)}%${clickable ? '<br/><i style="color:#718096">Click to see clients</i>' : ''}`;
        },
      },
      legend: { bottom: 0, left: 'center', type: 'scroll' as const, textStyle: { fontFamily: FONT_FAMILY, fontSize: 11, color: '#718096' }, itemWidth: 10, itemHeight: 10, itemGap: 12 },
      graphic: [
        { type: 'text', left: 'center', top: '32%', style: { text: String(data.total_conversations), textAlign: 'center', fill: '#2D3748', fontSize: 22, fontWeight: 700, fontFamily: FONT_FAMILY } },
        { type: 'text', left: 'center', top: '43%', style: { text: 'Conversations', textAlign: 'center', fill: '#A0AEC0', fontSize: 10, fontFamily: FONT_FAMILY } },
      ],
      series: [{ type: 'pie', radius: ['40%', '65%'], center: ['50%', '40%'], avoidLabelOverlap: true, itemStyle: { borderRadius: 4, borderColor: '#fff', borderWidth: 2 }, label: { show: true, formatter: '{b}\n{d}%', fontFamily: FONT_FAMILY, fontSize: 10, color: '#4A5568', lineHeight: 14 }, labelLine: { show: true, length: 8, length2: 12 }, emphasis: { label: { show: true, fontSize: 12, fontWeight: 'bold' }, scale: true, scaleSize: 5 }, data: pieData }],
    };
  };

  const hasData = data && data.unit_types && data.unit_types.length > 0;

  return (
    <>
      <ChartCard title="Unit-wise Interest Split" subtitle={preloaded ? "Click any segment to see client names" : "Loading client data..."} isLoading={isLoading} error={error}>
        {hasData ? (
          <ReactECharts option={getChartOption()} style={{ height: '340px', width: '100%', cursor: 'pointer' }} opts={{ renderer: 'svg' }} onEvents={{ click: (params: any) => { if (params.name) handleSliceClick(params.name); } }} />
        ) : (
          <Flex align="center" justify="center" minH="200px">
            <Text color="gray.400" fontSize="sm" fontFamily={FONT_FAMILY}>{!businessIdentifier ? 'Select a project to view data' : 'No data available'}</Text>
          </Flex>
        )}
      </ChartCard>
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(5px)" />
        <ModalContent>
          <ModalHeader fontFamily={FONT_FAMILY} fontSize="md">Clients interested in <Badge colorScheme="blue" fontSize="sm" ml={1}>{selectedUnit}</Badge></ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} maxH="400px" overflowY="auto">
            {isLoadingClients ? (
              <Flex justify="center" py={8}><Spinner /></Flex>
            ) : clients.length === 0 ? (
              <Text color="gray.500" fontSize="sm" textAlign="center" py={4}>No clients found for this unit type.</Text>
            ) : (
              <Table variant="simple" size="sm">
                <Thead><Tr><Th>#</Th><Th>Client Name</Th></Tr></Thead>
                <Tbody>
                  {clients.map((client, idx) => (
                    <Tr key={client.conversation_id}><Td>{idx + 1}</Td><Td fontWeight="medium">{client.client_name}</Td></Tr>
                  ))}
                </Tbody>
              </Table>
            )}
            <Text fontSize="xs" color="gray.400" mt={3} textAlign="right">{clients.length} client{clients.length !== 1 ? 's' : ''} found</Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default UnitInterestDonut;
